import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { useCallback, useEffect, useMemo } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { z } from "zod";

import ConsoleDialogContent from "@/components/console/common/ConsoleDialogContent";
import InputError from "@/components/console/common/InputError";
import CounterInput from "@/components/console/form/CounterInput";
import DateRangePicker from "@/components/console/form/DateRangePicker";
import Editor, { wrapImageWithLink } from "@/components/console/form/Editor";
import InputBox from "@/components/console/form/InputBox";
import Radio from "@/components/console/form/Radio";
import Toggle from "@/components/console/form/Toggle";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useDelPopup, useGetPopup, usePostPopupCreate, usePutPopup } from "@/service/console/popup";
import { usePopupStore } from "@/store/common/usePopupStore";

interface PopupPopProps {
    handleClose: () => void;
    open: boolean;
    handleComplete: () => void;
    type: "P" | "M";
    idx?: number;
}

export default function PopupPop({ handleClose, open, handleComplete, type, idx }: PopupPopProps) {
    const { data: configData, isLoading: isInitialLoading } = useGetPopup(idx?.toString() || "");
    const postPopupCreateMutation = usePostPopupCreate();
    const putPopupMutation = usePutPopup();
    const delPopupMutation = useDelPopup();
    const { setConfirmPop, setLoadingPop } = usePopupStore();

    // isFlg 값에따라 schema 변경
    const schema = useMemo(
        () =>
            z
                .object({
                    p_open: z.boolean(),
                    p_title: z.string().min(1, "팝업명을 입력해주세요."),
                    p_one_day: z.enum(["Y", "N"]),
                    isDate: z.enum(["Y", "N"]),
                    p_s_date: z.date().optional(),
                    p_e_date: z.date().optional(),
                    p_width_size: z.string().min(1, "팝업창 사이즈를 입력해주세요."),
                    p_height_size: z.string().min(1, "팝업창 사이즈를 입력해주세요."),
                    p_left_point: z.string().min(1, "팝업창 위치를 입력해주세요."),
                    p_top_point: z.string().min(1, "팝업창 위치를 입력해주세요."),
                    p_content: z.string().min(1, "내용을 입력해주세요."),
                })
                .superRefine((data, ctx) => {
                    if (data.isDate === "Y" && (!data.p_s_date || !data.p_e_date)) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: "기간을 설정해주세요.",
                            path: ["p_s_date"],
                        });
                    }
                }),
        [],
    );

    type FormValues = z.infer<typeof schema>;

    // 기본값
    const initialValues = useMemo<FormValues>(
        () => ({
            p_open: true,
            p_title: "",
            p_one_day: "Y",
            isDate: "N",
            p_width_size: "",
            p_height_size: "",
            p_left_point: "",
            p_top_point: "",
            p_content: "",
        }),
        [],
    );
    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        control,
        setValue,
    } = useForm<FormValues>({
        resolver: zodResolver(schema),
        defaultValues: initialValues,
    });
    const values = useWatch({ control });

    // 데이터 로딩 또는 저장,수정,삭제 중일 때 로딩 팝업 표시
    useEffect(() => {
        const isLoading =
            isInitialLoading ||
            postPopupCreateMutation.isPending ||
            putPopupMutation.isPending ||
            delPopupMutation.isPending;
        setLoadingPop(isLoading, true);
        return () => setLoadingPop(false);
    }, [
        isInitialLoading,
        postPopupCreateMutation.isPending,
        putPopupMutation.isPending,
        delPopupMutation.isPending,
        setLoadingPop,
    ]);

    // 팝업 상세 데이터 가져오기
    useEffect(() => {
        if (configData) {
            const data = configData.data;
            reset({
                p_open: data.p_open[0] === "Y" ? true : false,
                p_title: data.p_title,
                p_one_day: data.p_one_day[0],
                isDate: data.p_s_date || data.p_e_date ? "Y" : "N",
                p_s_date: data.p_s_date ? new Date(data.p_s_date) : undefined,
                p_e_date: data.p_e_date ? new Date(data.p_e_date) : undefined,
                p_width_size: data.p_width_size.toLocaleString(),
                p_height_size: data.p_height_size.toLocaleString(),
                p_left_point: data.p_left_point.toLocaleString(),
                p_top_point: data.p_top_point.toLocaleString(),
                p_content: data.p_content,
            });
        }
    }, [configData, reset]);

    // 저장 확인
    const handleConfirmSave = (data: FormValues) => {
        setConfirmPop(true, "저장하시겠습니까?", 2, () => onSubmit(data));
    };

    // 저장하기
    const onSubmit = (data: FormValues) => {
        const { isDate, ...restData } = data;
        const baseBody = {
            ...restData,
            p_type: type,
            p_open: data.p_open ? "Y" : "N",
            p_title: data.p_title,
            p_s_date: isDate === "Y" && data.p_s_date ? format(data.p_s_date, "yyyy.MM.dd") : "",
            p_e_date: isDate === "Y" && data.p_e_date ? format(data.p_e_date, "yyyy.MM.dd") : "",
            p_one_day: data.p_one_day,
            p_layer_pop: "1", //고정값
            p_width_size: Number(data.p_width_size.replace(/,/g, "")),
            p_height_size: Number(data.p_height_size.replace(/,/g, "")),
            p_left_point: Number(data.p_left_point.replace(/,/g, "")),
            p_top_point: Number(data.p_top_point.replace(/,/g, "")),
            p_scroll: "Y", //고정값
            p_link_target: "2", //고정값
            p_link_url: "", //고정값
            p_lang: "KR", //고정값
            p_content: wrapImageWithLink(data.p_content), // 이미지 링크 a 태그로 변환
        };
        // 팝업상세 수정하기
        if (idx) {
            const body = {
                ...baseBody,
                idx,
            };
            putPopupMutation.mutate(body, {
                onSuccess: () => {
                    setConfirmPop(true, "저장되었습니다.", 1, () => {
                        if (handleComplete) {
                            handleComplete(); // 완료 후 콜백 실행
                        }
                        handleCancel(); // 입력값 초기화 후 팝업닫기
                    });
                },
            });
        }
        // 팝업 등록하기
        else {
            const body = {
                ...baseBody,
            };
            postPopupCreateMutation.mutate(body, {
                onSuccess: () => {
                    setConfirmPop(true, "저장되었습니다.", 1, () => {
                        handleCancel(); // 입력값 초기화 후 팝업닫기
                        if (handleComplete) {
                            handleComplete(); // 완료 후 콜백 실행
                        }
                    });
                },
            });
        }
    };

    // 삭제 확인
    const handleConfirmDelete = () => {
        setConfirmPop(true, "삭제하시겠습니까?", 2, () => handleDelete());
    };

    // 삭제하기
    const handleDelete = () => {
        if (!idx) return;
        const body = { idx: [idx] };
        delPopupMutation.mutate(body, {
            onSuccess: () => {
                setConfirmPop(true, "삭제되었습니다.", 1, () => {
                    handleCancel(); // 입력값 초기화 후 팝업닫기
                    if (handleComplete) {
                        handleComplete(); // 완료 후 콜백 실행
                    }
                });
            },
        });
    };

    // 입력값 초기화
    const handleResetForm = useCallback(() => {
        reset(initialValues);
    }, [reset, initialValues]);

    // 취소버튼 클릭시 입력값 초기화 후 팝업닫기
    const handleCancel = () => {
        handleResetForm();
        handleClose();
    };

    // 팝업닫힐때 입력값 초기화
    useEffect(() => {
        if (!open) handleResetForm();
    }, [open, handleResetForm]);

    return (
        <ConsoleDialogContent
            title={
                <div className="flex items-center gap-[12px]">
                    <p>{type === "P" ? "PC" : "Mobile"} 팝업</p>
                    <Toggle
                        checked={values.p_open ?? true}
                        handleChange={checked => {
                            setValue("p_open", checked);
                        }}
                        onLabel="노출"
                        offLabel="중단"
                    />
                </div>
            }
            className="max-w-[800px]"
        >
            <form
                onSubmit={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSubmit(handleConfirmSave)();
                }}
            >
                <ScrollArea className="max-h-[75vh] min-h-[300px] border-b border-[#ddd]">
                    <div className="flex flex-col gap-[20px] p-[16px_40px]">
                        <ul className="flex flex-col gap-[20px] rounded-[8px] bg-white p-[24px_28px]">
                            <li className="flex flex-col gap-[8px]">
                                <label className="font-[500]" htmlFor="tit">
                                    팝업명
                                    <span className="pl-[5px] text-[15px] font-[400] text-console">*</span>
                                </label>
                                <div>
                                    <CounterInput
                                        {...register("p_title")}
                                        id="tit"
                                        maxLength={50}
                                        placeholder="팝업명을 입력해주세요."
                                        value={values.p_title || ""}
                                        className="flex-1"
                                    />
                                    <InputError message={errors.p_title?.message} />
                                </div>
                            </li>
                            <li className="flex gap-[24px]">
                                <div className="flex w-1/2 flex-col gap-[12px]">
                                    <p className="pt-[5px] font-[500]">
                                        팝업창 위치
                                        <span className="pl-[5px] text-[15px] font-[400] text-console">*</span>
                                    </p>
                                    <div>
                                        <ul className="flex gap-[4px]">
                                            <li className="flex-1">
                                                <InputBox
                                                    {...register("p_left_point")}
                                                    className="pl-[40px] text-right"
                                                    numberInput
                                                    thousandSeparator
                                                    value={values.p_left_point}
                                                >
                                                    <p className="absolute left-[12px] top-1/2 -translate-y-1/2 text-[14px] text-[#999]">
                                                        가로
                                                    </p>
                                                </InputBox>
                                            </li>
                                            <li className="flex-1">
                                                <InputBox
                                                    {...register("p_top_point")}
                                                    className="pl-[40px] text-right"
                                                    numberInput
                                                    thousandSeparator
                                                    value={values.p_top_point}
                                                >
                                                    <p className="absolute left-[12px] top-1/2 -translate-y-1/2 text-[14px] text-[#999]">
                                                        세로
                                                    </p>
                                                </InputBox>
                                            </li>
                                        </ul>
                                        <InputError
                                            message={errors.p_left_point?.message || errors.p_top_point?.message}
                                        />
                                    </div>
                                </div>
                                <div className="flex w-1/2 flex-col gap-[12px]">
                                    <div className="flex items-center justify-between">
                                        <p className="pt-[5px] font-[500]">
                                            팝업창 사이즈
                                            <span className="pl-[5px] text-[15px] font-[400] text-console">*</span>
                                        </p>
                                        {/* <p className="text-[14px] font-[500] text-[#999]">권장 : 300 * 400 px</p> */}
                                    </div>
                                    <div>
                                        <ul className="flex gap-[4px]">
                                            <li className="flex-1">
                                                <InputBox
                                                    {...register("p_width_size")}
                                                    className="pl-[40px] text-right"
                                                    numberInput
                                                    thousandSeparator
                                                    value={values.p_width_size}
                                                >
                                                    <p className="absolute left-[12px] top-1/2 -translate-y-1/2 text-[14px] text-[#999]">
                                                        가로
                                                    </p>
                                                </InputBox>
                                            </li>
                                            <li className="flex-1">
                                                <InputBox
                                                    {...register("p_height_size")}
                                                    className="pl-[40px] text-right"
                                                    numberInput
                                                    thousandSeparator
                                                    value={values.p_height_size}
                                                >
                                                    <p className="absolute left-[12px] top-1/2 -translate-y-1/2 text-[14px] text-[#999]">
                                                        세로
                                                    </p>
                                                </InputBox>
                                            </li>
                                        </ul>
                                        <InputError
                                            message={errors.p_width_size?.message || errors.p_height_size?.message}
                                        />
                                    </div>
                                </div>
                            </li>
                            <li className="flex gap-[24px]">
                                <div className="flex w-1/2 flex-col gap-[12px]">
                                    <p className="pt-[5px] font-[500]">
                                        팝업창 1일 유효 여부
                                        <span className="pl-[5px] text-[15px] font-[400] text-console">*</span>
                                    </p>
                                    <Controller
                                        name="p_one_day"
                                        control={control}
                                        render={({ field }) => (
                                            <ul className="flex justify-between">
                                                <li className="flex-1">
                                                    <Radio
                                                        {...field}
                                                        value="Y"
                                                        checked={field.value === "Y"}
                                                        txt="사용"
                                                    />
                                                </li>
                                                <li className="flex-1">
                                                    <Radio
                                                        {...field}
                                                        value="N"
                                                        checked={field.value === "N"}
                                                        txt="사용 안 함"
                                                    />
                                                </li>
                                            </ul>
                                        )}
                                    />
                                </div>
                            </li>
                            <li className="flex flex-col gap-[12px]">
                                <p className="pt-[5px] font-[500]">
                                    노출기간 설정
                                    <span className="pl-[5px] text-[15px] font-[400] text-console">*</span>
                                </p>
                                <Controller
                                    name="isDate"
                                    control={control}
                                    render={({ field }) => (
                                        <ul className="flex w-1/2 justify-between">
                                            <li className="flex-1">
                                                <Radio
                                                    {...field}
                                                    value="N"
                                                    checked={field.value === "N"}
                                                    txt="상시노출"
                                                />
                                            </li>
                                            <li className="flex-1">
                                                <Radio
                                                    {...field}
                                                    value="Y"
                                                    checked={field.value === "Y"}
                                                    txt="기간노출"
                                                />
                                            </li>
                                        </ul>
                                    )}
                                />
                                {/* 기간노출일때만 날짜선택가능 */}
                                {values.isDate === "Y" && (
                                    <div>
                                        <div className="mt-[8px] bg-[#FAFAFD] p-[8px_20px]">
                                            <DateRangePicker
                                                startDate={values.p_s_date ?? null}
                                                endDate={values.p_e_date ?? null}
                                                setStartDate={date => date && setValue("p_s_date", date)}
                                                setEndDate={date => date && setValue("p_e_date", date)}
                                            />
                                        </div>
                                        <InputError message={errors.p_s_date?.message} />
                                    </div>
                                )}
                                {/* //기간노출일때만 날짜선택가능 */}
                            </li>
                        </ul>
                        <div className="rounded-[8px] bg-white p-[24px_28px]">
                            <Editor
                                value={values.p_content || ""}
                                onChange={cont => {
                                    setValue("p_content", cont);
                                }}
                            />
                            <InputError message={errors.p_content?.message} />
                        </div>
                    </div>
                </ScrollArea>
                <div className={`flex items-center p-[16px_40px] ${idx ? "justify-between" : "justify-end"}`}>
                    {idx && (
                        <button
                            type="button"
                            className="text-[14px] text-[#999] underline"
                            onClick={handleConfirmDelete}
                        >
                            삭제
                        </button>
                    )}
                    <div className="flex gap-[10px]">
                        <button
                            type="button"
                            className="h-[40px] w-[160px] rounded-[5px] border border-[#ddd] bg-white font-[500] text-[#666]"
                            onClick={handleCancel}
                        >
                            취소
                        </button>
                        <button
                            type="submit"
                            className="h-[40px] w-[160px] rounded-[5px] bg-[#262A34] font-[500] text-white"
                        >
                            저장
                        </button>
                    </div>
                </div>
            </form>
        </ConsoleDialogContent>
    );
}
